import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { GeoJSONSource, Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl'
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import { Box, type SxProps, type Theme } from '@mui/material'
import {
  BasemapSwitch, MapCanvas, MapControl, STATUS_COLOURS, ZoomControl, centroid, fitToFeatures, tokens, type Basemap,
} from '@agribot/polygon-checks-ui'
import type { Origin } from '../types'

/**
 * The sourcing maps: one frame (basemap switch, zoom, legend) for every map
 * of origins, and one map of origins for the supplier's origins screen, the
 * buyer's review and the campaign's origins map.
 */

export interface LegendEntry {
  label: string
  /** The swatch; none for a line of plain words, such as what size means. */
  swatch?: SxProps<Theme>
}

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** A dot for a legend, outlined like the map draws its points. */
export const dot = (colour: string): SxProps<Theme> => ({
  borderRadius: '50%', backgroundColor: colour, border: `2px solid ${tokens.color.ink0}`, boxShadow: `0 0 0 1px ${tokens.color.ink300}`,
})

export interface MapFrameProps {
  label: string
  /** Called once the style has loaded, with the map, to add sources and layers. */
  onReady: (map: MapLibreMap) => void
  onRemove: () => void
  onMapClick?: (event: MapMouseEvent) => void
  /** Held by the frame's zoom control. */
  map: MapLibreMap | null
  legend: LegendEntry[]
  basemapLabels?: { map: string; satellite: string }
  /** The map controls' names for screen readers, in the reader's language. */
  controlLabels?: { basemap: string; zoomIn: string; zoomOut: string }
  initialCentre?: [number, number]
  initialZoom?: number
  showReadout?: boolean
  cursor?: string
  /** A fixed height in a bordered box, or `fill` to take the parent's room. */
  height?: number | string | 'fill'
  children?: ReactNode
}

/** A map with a basemap switch, zoom buttons and a legend, in a labelled region. */
export function MapFrame({
  label, onReady, onRemove, onMapClick, map, legend, basemapLabels = { map: 'Map', satellite: 'Satellite' },
  controlLabels = { basemap: 'Basemap', zoomIn: 'Zoom in', zoomOut: 'Zoom out' },
  initialCentre = [-75, -10], initialZoom = 3, showReadout = false, cursor, height = 420, children,
}: MapFrameProps) {
  const [basemap, setBasemap] = useState<Basemap>('map')
  const fill = height === 'fill'
  return (
    <Box
      role="region"
      aria-label={label}
      sx={fill
        ? { position: 'relative', flex: 1, display: 'flex', minHeight: 320 }
        : { position: 'relative', height, display: 'flex', borderRadius: `${tokens.radius.md}px`, overflow: 'hidden', border: `1px solid ${tokens.color.ink200}` }}
    >
      <MapCanvas
        onReady={onReady}
        onRemove={onRemove}
        onMapClick={onMapClick}
        basemap={basemap}
        initialCentre={initialCentre}
        initialZoom={initialZoom}
        showReadout={showReadout}
        cursor={cursor}
      >
        <BasemapSwitch
          value={basemap}
          onChange={setBasemap}
          options={[{ value: 'map', label: basemapLabels.map }, { value: 'satellite', label: basemapLabels.satellite }]}
          label={controlLabels.basemap}
        />
        <ZoomControl map={map} sx={{ top: 64 }} labels={controlLabels} />
        <MapControl
          aria-hidden
          sx={{ left: fill ? 16 : 12, bottom: fill ? 16 : 12, top: 'auto', flexDirection: 'column', gap: 1, px: 2.5, py: 2, font: `400 12px ${tokens.font.sans}` }}
        >
          {legend.map((l) => (
            l.swatch ? (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 10, height: 10, flex: 'none', ...l.swatch } as SxProps<Theme>} />
                {l.label}
              </Box>
            ) : <Box key={l.label} sx={{ color: 'text.secondary' }}>{l.label}</Box>
          ))}
        </MapControl>
        {children}
      </MapCanvas>
    </Box>
  )
}

/**
 * What a click on the map means: nothing but choosing an origin (`view`),
 * placing a new point (`pin`), moving one (`move`), or drawing an area's
 * outline (`area`).
 */
export type MapMode = 'view' | 'pin' | 'move' | 'area'

const CHECK_COLOUR = {
  passed: STATUS_COLOURS.passed,
  failed: STATUS_COLOURS.rejected,
  pending: STATUS_COLOURS.excluded,
} as const

/** Teal while drawing, as on the app's own drawing screen. */
const DRAW_STYLE = {
  fillColor: tokens.color.brandTeal,
  fillOpacity: 0.14,
  outlineColor: tokens.color.brandTeal,
  outlineWidth: 2,
  closingPointColor: tokens.color.brandTeal,
  closingPointWidth: 5,
  closingPointOutlineColor: tokens.color.ink0,
  closingPointOutlineWidth: 2,
  coordinatePointColor: tokens.color.ink0,
  coordinatePointWidth: 4,
  coordinatePointOutlineColor: tokens.color.brandTeal,
  coordinatePointOutlineWidth: 2,
} as const

/**
 * Each origin, plus a marker at the centre of every area: a plantation of a
 * few hundred hectares is invisible at the zoom that shows a whole supply
 * base, and an origin missing from the map reads as one never declared.
 */
function toFeatures(origins: Origin[], selected: string | null, flagged: Set<string>): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: origins.flatMap((o) => {
      const properties = {
        id: o.id,
        name: o.name,
        fill: CHECK_COLOUR[o.check.state].fill,
        stroke: CHECK_COLOUR[o.check.state].stroke,
        selected: o.id === selected,
        flagged: flagged.has(o.id),
      }
      const own: GeoJSON.Feature = { type: 'Feature', geometry: o.geometry, properties: { ...properties, areaMarker: false } }
      const centre = o.geometry.type === 'Polygon' ? centroid(o.geometry) : null
      return centre
        ? [own, { type: 'Feature', geometry: { type: 'Point', coordinates: centre }, properties: { ...properties, areaMarker: true } }]
        : [own]
    }),
  }
}

export interface OriginMapLabels {
  region: string
  map: string
  satellite: string
  passed: string
  failed: string
  pending: string
  flagged: string
  plots: string
  basemap: string
  zoomIn: string
  zoomOut: string
}

const ENGLISH: Omit<OriginMapLabels, 'region'> = {
  map: 'Map', satellite: 'Satellite', basemap: 'Basemap', zoomIn: 'Zoom in', zoomOut: 'Zoom out',
  passed: 'Checks passed', failed: 'Check failed', pending: 'Not checked yet',
  flagged: 'Flagged', plots: 'Farm plots, by check result',
}

export interface OriginMapProps {
  origins: Origin[]
  /** The region's name, and the legend's words in the reader's language (English by default). */
  labels: Partial<OriginMapLabels> & { region: string }
  selected?: string | null
  onSelect?: (originId: string) => void
  /** Origins a reviewer flagged, ringed. */
  flagged?: Set<string>
  /** The selected origin's farm plots, drawn under the origins by check result. */
  plots?: GeoJSON.FeatureCollection | null
  mode?: MapMode
  /** A point placed in `pin` or `move` mode. */
  onPoint?: (lngLat: [number, number]) => void
  /** An outline closed in `area` mode. */
  onArea?: (polygon: GeoJSON.Polygon) => void
  /** Where a point being added sits, before it is saved. */
  draft?: GeoJSON.Point | GeoJSON.Polygon | null
  /** Changes when the map should fit its origins again, e.g. another material. */
  fitKey?: string
  height?: number | string | 'fill'
  padding?: number
  showReadout?: boolean
}

const NO_FLAGS = new Set<string>()

/**
 * Every origin on one map, coloured by its check, so a point in the sea or
 * in the wrong country is obvious before anyone reads a list. The reason is
 * always in the list as well: colour is never the only signal.
 */
export function OriginMap({
  origins, labels: given, selected = null, onSelect, flagged = NO_FLAGS, plots = null, mode = 'view', onPoint, onArea,
  draft = null, fitKey, height = 420, padding = 56, showReadout = false,
}: OriginMapProps) {
  const labels = { ...ENGLISH, ...given }
  const [map, setMap] = useState<MapLibreMap | null>(null)
  const features = useMemo(() => toFeatures(origins, selected, flagged), [origins, selected, flagged])

  // Read by handlers wired once.
  const modeRef = useRef(mode)
  modeRef.current = mode
  const onPointRef = useRef(onPoint)
  onPointRef.current = onPoint
  const onAreaRef = useRef(onArea)
  onAreaRef.current = onArea
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  const onReady = useCallback((instance: MapLibreMap) => {
    // Farm plots first, so origins draw on top of them.
    instance.addSource('plots', { type: 'geojson', data: EMPTY })
    const plotColour = ['match', ['get', 'status'], 'passed', tokens.color.brandGreen700, 'flagged', tokens.color.warning, 'rejected', tokens.color.danger, tokens.color.ink400] as never
    instance.addLayer({ id: 'plots-fill', type: 'fill', source: 'plots', paint: { 'fill-color': plotColour, 'fill-opacity': 0.3 } })
    instance.addLayer({ id: 'plots-line', type: 'line', source: 'plots', paint: { 'line-color': plotColour, 'line-width': 1 } })
    // A plot of a few hectares vanishes at a regional zoom: a dot each until it can be seen.
    instance.addSource('plot-dots', { type: 'geojson', data: EMPTY })
    instance.addLayer({ id: 'plot-dots', type: 'circle', source: 'plot-dots', maxzoom: 12, paint: { 'circle-radius': 3, 'circle-color': plotColour, 'circle-stroke-color': tokens.color.ink0, 'circle-stroke-width': 1 } })

    instance.addSource('origins', { type: 'geojson', data: EMPTY })
    instance.addSource('draft', { type: 'geojson', data: EMPTY })
    const isPolygon = ['==', ['geometry-type'], 'Polygon'] as never
    const ring = ['case', ['get', 'selected'], tokens.color.brandTeal, ['get', 'flagged'], tokens.color.warning]
    const circlePaint = {
      'circle-radius': ['case', ['get', 'selected'], 9, 7],
      'circle-color': ['get', 'stroke'],
      'circle-stroke-color': [...ring, tokens.color.ink0],
      'circle-stroke-width': ['case', ['get', 'selected'], 3, ['get', 'flagged'], 3, 2],
    } as never
    instance.addLayer({ id: 'origins-fill', type: 'fill', source: 'origins', filter: isPolygon, paint: { 'fill-color': ['get', 'fill'] } })
    instance.addLayer({
      id: 'origins-line', type: 'line', source: 'origins', filter: isPolygon,
      paint: {
        'line-color': [...ring, ['get', 'stroke']] as never,
        'line-width': ['case', ['get', 'selected'], 3, ['get', 'flagged'], 3, 1.5],
      },
    })
    instance.addLayer({
      id: 'origins-area-marker', type: 'circle', source: 'origins', maxzoom: 11,
      filter: ['all', ['==', ['geometry-type'], 'Point'], ['get', 'areaMarker']], paint: circlePaint,
    })
    instance.addLayer({
      id: 'origins-point', type: 'circle', source: 'origins',
      filter: ['all', ['==', ['geometry-type'], 'Point'], ['!', ['get', 'areaMarker']]], paint: circlePaint,
    })
    instance.addLayer({
      id: 'draft-fill', type: 'fill', source: 'draft', filter: isPolygon,
      paint: { 'fill-color': tokens.color.brandTeal, 'fill-opacity': 0.18 },
    })
    instance.addLayer({
      id: 'draft-point', type: 'circle', source: 'draft', filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 8, 'circle-color': tokens.color.brandTeal, 'circle-stroke-color': tokens.color.ink0, 'circle-stroke-width': 3 },
    })
    for (const layer of ['origins-fill', 'origins-point', 'origins-area-marker']) {
      instance.on('mouseenter', layer, () => { if (modeRef.current === 'view') instance.getCanvas().style.cursor = 'pointer' })
      instance.on('mouseleave', layer, () => { if (modeRef.current === 'view') instance.getCanvas().style.cursor = '' })
    }
    setMap(instance)
  }, [])

  // The data, whenever it changes.
  useEffect(() => {
    ;(map?.getSource('origins') as GeoJSONSource | undefined)?.setData(features)
  }, [map, features])

  useEffect(() => {
    ;(map?.getSource('draft') as GeoJSONSource | undefined)?.setData(
      draft ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: draft, properties: {} }] } : EMPTY,
    )
  }, [map, draft])

  useEffect(() => {
    ;(map?.getSource('plots') as GeoJSONSource | undefined)?.setData(plots ?? EMPTY)
    ;(map?.getSource('plot-dots') as GeoJSONSource | undefined)?.setData(plots ? {
      type: 'FeatureCollection',
      features: plots.features.flatMap((f) => {
        const c = centroid(f.geometry)
        return c ? [{ type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: c }, properties: f.properties }] : []
      }),
    } : EMPTY)
  }, [map, plots])

  // Fit once the map is up, and again when the subject changes.
  useEffect(() => {
    if (!map) return
    if (features.features.length) {
      fitToFeatures(map, features, padding)
      if (origins.length === 1 && origins[0]!.kind === 'point') map.setZoom(Math.min(map.getZoom(), 8))
    } else {
      map.jumpTo({ center: [-75, -10], zoom: 3 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey])

  // One decision about where to look, so two moves never race: the selected
  // origin's farm plots once they arrive, otherwise the origin itself.
  useEffect(() => {
    if (!map || !selected) return
    if (plots?.features.length) {
      fitToFeatures(map, plots, 80)
      return
    }
    const one = features.features.find((f) => f.properties?.['id'] === selected && !f.properties?.['areaMarker'])
    if (!one) return
    // A point is judged against its surroundings (coast, border, town), so
    // centre it at a regional zoom rather than the street level a fit gives.
    if (one.geometry.type === 'Point') map.flyTo({ center: one.geometry.coordinates as [number, number], zoom: Math.max(map.getZoom(), 8), duration: 400 })
    else fitToFeatures(map, { type: 'FeatureCollection', features: [one] }, 120)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selected, plots])

  // Drawing an area: terra-draw only while that mode is on.
  useEffect(() => {
    if (!map || mode !== 'area') return
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [new TerraDrawPolygonMode({ styles: DRAW_STYLE, showCoordinatePoints: true })],
    })
    draw.start()
    draw.setMode('polygon')
    draw.on('finish', (id) => {
      const geometry = draw.getSnapshotFeature(id)?.geometry
      if (geometry?.type === 'Polygon') onAreaRef.current?.(geometry)
      draw.clear()
    })
    return () => draw.stop()
  }, [map, mode])

  const onMapClick = useCallback((event: MapMouseEvent) => {
    const current = modeRef.current
    if (current === 'pin' || current === 'move') {
      onPointRef.current?.([Number(event.lngLat.lng.toFixed(5)), Number(event.lngLat.lat.toFixed(5))])
      return
    }
    if (current !== 'view') return
    const hit = event.target.queryRenderedFeatures(event.point, { layers: ['origins-point', 'origins-area-marker', 'origins-fill'] })[0]
    const id = hit?.properties?.['id']
    if (typeof id === 'string') onSelectRef.current?.(id)
  }, [])

  const legend: LegendEntry[] = [
    ...(['passed', 'failed', 'pending'] as const).map((state) => ({ label: labels[state], swatch: dot(CHECK_COLOUR[state].stroke) })),
    ...(flagged.size ? [{ label: labels.flagged, swatch: { borderRadius: '50%', border: `3px solid ${tokens.color.warning}` } }] : []),
    ...(plots?.features.length
      ? [{ label: labels.plots, swatch: { borderRadius: '2px', backgroundColor: 'rgba(61,150,112,0.3)', border: `1px solid ${tokens.color.brandGreen700}` } }]
      : []),
  ]

  return (
    <MapFrame
      label={labels.region}
      map={map}
      onReady={onReady}
      onRemove={() => setMap(null)}
      onMapClick={onMapClick}
      legend={legend}
      basemapLabels={{ map: labels.map, satellite: labels.satellite }}
      controlLabels={{ basemap: labels.basemap, zoomIn: labels.zoomIn, zoomOut: labels.zoomOut }}
      initialZoom={height === 'fill' ? 2 : 3}
      showReadout={showReadout}
      cursor={mode === 'view' ? '' : 'crosshair'}
      height={height}
    />
  )
}
